/**
 * Assets module: the Asset Registry plus the content index and seed, which
 * every later module needs (level list, profile links, Resume.pdf path).
 */
import { CoreModule } from "../core/CoreModule";
import { defineModule } from "../core/module";
import { AssetRegistry } from "./AssetRegistry";
import { folderForKind, LEVEL_FOLDERS } from "./content";
import type { ContentIndex } from "./content";
import { INDEX_REF, registerContentLoaders, SEED_REF } from "./contentLoaders";
import { HttpFetcher } from "./Fetcher";
import type { Fetcher } from "./Fetcher";

export interface AssetsOptions {
  readonly fetcher: Fetcher;
  /** URL prefix of the content folder, including the Pages base path. */
  readonly contentRoot: string;
}

export const AssetsModule = defineModule({
  name: "Assets",
  dependsOn: [CoreModule],
  options: (): AssetsOptions => ({
    fetcher: new HttpFetcher(),
    contentRoot: `${import.meta.env.BASE_URL}content/`,
  }),
  init: async ({ deps: { Core }, options }) => {
    const registry = new AssetRegistry({
      fetcher: options.fetcher,
      contentRoot: options.contentRoot,
      log: Core.log,
    });
    registerContentLoaders(registry);

    const [index, seed] = await Promise.all([registry.load(INDEX_REF), registry.load(SEED_REF)]);
    Core.log.display("AssetRegistry", describeScan(index));

    return { registry, index, seed };
  },
});

/** "scanned /Content/Roles (5) /Content/Projects (2) /Content/Education (1)", from the real index. */
function describeScan(index: ContentIndex): string {
  const counts = new Map<string, number>();
  for (const level of index.levels) {
    const folder = folderForKind(level.kind);
    counts.set(folder, (counts.get(folder) ?? 0) + 1);
  }

  const parts = LEVEL_FOLDERS.map((folder) => `/Content/${folder} (${counts.get(folder) ?? 0})`);
  return `scanned ${parts.join(" ")}`;
}
